/**
 * Pricing Page for trAInedup.ai
 * Displays subscription tiers with features and pricing
 */

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Star, Zap, Crown } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { ROUTES, SUBSCRIPTION_PRICES, TIER_FEATURES } from '../utils/constants';
import { detectCurrency } from '../utils/currency';
import { redirectToCheckout, getPriceIdFromTier } from '../services/stripe/subscriptions';
import { SUBSCRIPTION_TIERS } from '../services/stripe/config';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../services/firebase/config';
import { SEOHead } from '../components/seo/SEOHead';
import { StructuredData, createProductSchema } from '../components/seo/StructuredData';

/**
 * Pricing page component showcasing three subscription tiers
 * Implements clear value proposition and feature comparison
 */
export const PricingPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromTwelveMonth = searchParams.get('from') === 'twelve-month';
  const [showStackingConfirm, setShowStackingConfirm] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string>('');

  // Check if user is on 12-month package
  const isTwelveMonthUser = user?.subscriptionTier === SUBSCRIPTION_TIERS.TWELVE_MONTH_PACKAGE;

  // Stripe checkout handlers for each plan
  const handleSubscribe = async (tier: keyof typeof SUBSCRIPTION_TIERS) => {
    let tierKey: string = '';
    switch (tier) {
      case 'STANDARD':
        tierKey = SUBSCRIPTION_TIERS.STANDARD;
        break;
      case 'PREMIUM':
        tierKey = SUBSCRIPTION_TIERS.PREMIUM;
        break;
      case 'CORPORATE':
        // Corporate is now a bespoke contact flow
        navigate(ROUTES.corporateRequest);
        return;
      default:
        return;
    }

    if (!isAuthenticated) {
      // Redirect to register page with plan as query param
      navigate(`${ROUTES.register}?plan=${tier}`);
      return;
    }

    // If user is on 12-month package and coming from billing page, show stacking confirmation
    if (isTwelveMonthUser && fromTwelveMonth) {
      setSelectedTier(tierKey);
      setShowStackingConfirm(true);
      return;
    }

    try {
      const priceId = getPriceIdFromTier(tierKey as any);
      await redirectToCheckout(priceId, tierKey as any);
    } catch (err) {
      // Enhanced error logging for debugging
      if (err instanceof Error) {
        console.error('Stripe Checkout Error:', err.message, err.stack);
        alert('Stripe error: ' + err.message);
      } else {
        console.error('Stripe Checkout Error:', err);
        alert('Unknown Stripe error. See console for details.');
      }
    }
  };

  // Handle stacked subscription purchase
  const handleConfirmStackedSubscription = async () => {
    try {
      const createStackedSubscription = httpsCallable(functions, 'createStackedMonthlySubscription');
      const priceId = getPriceIdFromTier(selectedTier as any);
      
      const result = await createStackedSubscription({ 
        priceId, 
        tier: selectedTier 
      }) as any;

      if (result.data.checkoutUrl) {
        window.location.href = result.data.checkoutUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Stacked subscription error:', err);
      alert('Unable to create subscription. Please try again.');
      setShowStackingConfirm(false);
    }
  };

  const detectedCurrency = detectCurrency();

  const standardProductSchema = createProductSchema({
    name: 'trAInedup.ai Standard Plan',
    description: 'Access to AI courses, personalized learning paths, and community support.',
    price: SUBSCRIPTION_PRICES.standard.display,
    currency: detectedCurrency,
  });

  const premiumProductSchema = createProductSchema({
    name: 'trAInedup.ai Premium Plan',
    description: 'Everything in Standard plus 1-on-1 expert consultations, priority support, and advanced features.',
    price: SUBSCRIPTION_PRICES.premium.display,
    currency: detectedCurrency,
  });

  return (
    <Layout>
      <SEOHead
        title="Pricing - Affordable Plans to Catch Up on AI"
        description="Feeling left behind by AI? It's not too late, or expensive, to catch up. Start with our free Skills Assessment, then choose Standard or Premium. All plans include personalised learning paths and expert-curated content. No tech background required."
        canonical="https://trainedup.ai/pricing"
        keywords={['AI training pricing', 'AI course subscription', 'affordable AI learning', 'AI education plans']}
      />
      <StructuredData data={standardProductSchema} />
      <StructuredData data={premiumProductSchema} />

      {/* Stacking Confirmation Modal */}
      {showStackingConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Confirm Subscription Stacking</h3>
            <p className="text-neutral-600 mb-4">
              Your monthly subscription will be queued and will automatically start on{' '}
              <strong>{user?.subscriptionEndDate ? new Date(user.subscriptionEndDate).toLocaleDateString('en-GB') : 'your 12-month expiry date'}</strong>.
            </p>
            <p className="text-neutral-600 mb-6">
              You'll be charged today, but billing won't start until your 12-month package expires. This ensures seamless continuity with no gaps in access.
            </p>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowStackingConfirm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmStackedSubscription}
                className="flex-1"
              >
                Confirm & Continue
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-20">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-montserrat font-bold text-4xl md:text-5xl text-neutral-800 mb-6">
              Catch Up on AI, at a Price That Works for You
            </h1>
            <p className="text-xl text-neutral-600 mb-8">
              It's not too late to get up to speed. Start with our free Skills Assessment, then pick the plan that gets you there.
            </p>
            <p className="text-lg text-neutral-600">
              All plans include our core AI training content. Upgrade anytime for 1-on-1 consultations and advanced features.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            
            {/* Basic Plan  - Most Popular */}
            <Card className="relative border-2 border-primary" padding="lg" hover>
              {/* Popular Badge */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-primary text-white px-4 py-2 rounded-full text-sm font-semibold">
                  Most Popular
                </div>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                  <Star className="w-8 h-8 text-primary-100" />
                </div>
                <h3 className="font-montserrat font-bold text-2xl text-neutral-800 mb-2">
                  Basic
                </h3>
                <p className="text-neutral-600 mb-6">
                  Perfect for individuals getting started with AI
                </p>
                
                <div className="mb-8">
                  <span className="text-4xl font-bold text-neutral-800">
                    {SUBSCRIPTION_PRICES.standard.display}
                  </span>
                  <span className="text-neutral-600">/month</span>
                </div>

                <Button variant="primary" size="lg" fullWidth onClick={() => handleSubscribe('STANDARD')}>
                  Get Started
                </Button>
              </div>

              <div className="mt-8">
                <h4 className="font-semibold text-neutral-800 mb-4">What's included:</h4>
                <ul className="space-y-3">
                  {TIER_FEATURES.standard.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-neutral-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>

            {/* Premium Plan */}
            <Card className="relative" padding="lg" hover>
              <div className="text-center">
                <div className="w-16 h-16 bg-secondary-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-montserrat font-bold text-2xl text-neutral-800 mb-2">
                  Premium
                </h3>
                <p className="text-neutral-600 mb-6">
                  For people who want tailored courses and 1-on-1 guidance
                </p>
                
                <div className="mb-8">
                  <span className="text-4xl font-bold text-neutral-800">
                    {SUBSCRIPTION_PRICES.premium.display}
                  </span>
                  <span className="text-neutral-600">/month</span>
                </div>

                <Button variant="secondary" size="lg" fullWidth onClick={() => handleSubscribe('PREMIUM')}>
                  Get Started
                </Button>
              </div>

              <div className="mt-8">
                <h4 className="font-semibold text-neutral-800 mb-4">Everything in Basic, plus:</h4>
                <ul className="space-y-3">
                  {TIER_FEATURES.premium.slice(1).map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-neutral-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>

            {/* Corporate Plan */}
            <Card className="relative" padding="lg" hover>
              <div className="text-center">
                <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Crown className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-montserrat font-bold text-2xl text-neutral-800 mb-2">
                  Corporate
                </h3>
                <p className="text-neutral-600 mb-6">
                  Send a request if your organisation needs a customised solution.
                </p>
                
                <div className="mb-8">
                  <span className="text-4xl font-bold text-neutral-800">
                    POA
                  </span>
                </div>

                <Button 
                  variant="accent" 
                  size="lg" 
                  fullWidth 
                  onClick={() => navigate(ROUTES.corporateRequest)}
                >
                  Request Corporate Package
                </Button>
              </div>

              <div className="mt-8">
                <h4 className="font-semibold text-neutral-800 mb-4">A bespoke package that includes:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-neutral-600">Training packs designed for your organisation</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-neutral-600">Flexible delivery: workshops, self-paced, or blended</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-neutral-600">Expert consultations and ongoing support</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-neutral-600">Scalable solutions for teams of any size</span>
                  </li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-montserrat font-bold text-3xl text-center text-neutral-800 mb-12">
              Compare Plans
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-neutral-200">
                    <th className="text-left py-4 px-6 font-semibold text-neutral-800">Features</th>
                    <th className="text-center py-4 px-6 font-semibold text-neutral-800">Basic</th>
                    <th className="text-center py-4 px-6 font-semibold text-neutral-800">Premium</th>
                    <th className="text-center py-4 px-6 font-semibold text-neutral-800">Corporate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-neutral-200">
                    <td className="py-4 px-6 text-neutral-700">Monthly AI training packs</td>
                    <td className="text-center py-4 px-6"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="text-center py-4 px-6"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="text-center py-4 px-6"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-neutral-200">
                    <td className="py-4 px-6 text-neutral-700">Skills Assessments</td>
                    <td className="text-center py-4 px-6"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="text-center py-4 px-6"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="text-center py-4 px-6"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-neutral-200">
                    <td className="py-4 px-6 text-neutral-700">Expert consultations</td>
                    <td className="text-center py-4 px-6 text-neutral-400">—</td>
                    <td className="text-center py-4 px-6 text-neutral-600">2/month</td>
                    <td className="text-center py-4 px-6 text-neutral-600">Unlimited</td>
                  </tr>
                  <tr className="border-b border-neutral-200">
                    <td className="py-4 px-6 text-neutral-700">Custom content</td>
                    <td className="text-center py-4 px-6 text-neutral-400">—</td>
                    <td className="text-center py-4 px-6 text-neutral-400">—</td>
                    <td className="text-center py-4 px-6"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 text-neutral-700">Support level</td>
                    <td className="text-center py-4 px-6 text-neutral-600">Email</td>
                    <td className="text-center py-4 px-6 text-neutral-600">Priority</td>
                    <td className="text-center py-4 px-6 text-neutral-600">Dedicated</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-neutral-100">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-montserrat font-bold text-3xl text-center text-neutral-800 mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  Why can't I register and instead I'm told to join the waiting list?
                </h3>
                <p className="text-neutral-600">
                  We limit the number of active members to make sure every user receives the best possible service and support. Once we reach full capacity, registration temporarily closes. Don't worry, if you join the waiting list, we'll notify you as soon as a space becomes available or when we expand capacity.
                </p>
              </Card>

              <Card>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  Is my paid membership tax deductible?
                </h3>
                <p className="text-neutral-600">
                  In many countries, your membership may be tax-deductible if you're using what you learn for professional or business purposes. However, please note that this does not constitute formal tax advice. We recommend checking with a qualified tax professional for guidance based on your personal circumstances.
                </p>
              </Card>

              <Card>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  Can I cancel or change my plan at anytime?
                </h3>
                <p className="text-neutral-600">
                  Yes! You can cancel, upgrade or downgrade your plan at any time. Changes take effect at your next billing cycle.
                </p>
              </Card>

              <Card>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  Is there a free trial?
                </h3>
                <p className="text-neutral-600">
                  No, but we do offer a 7-day money-back guarantee.
                </p>
              </Card>

              <Card>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  What if I'm not satisfied?
                </h3>
                <p className="text-neutral-600">
                  We offer a 7-day money-back guarantee. If you're not completely satisfied, we'll refund your payment.
                </p>
              </Card>

              <Card>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  Do I need technical experience?
                </h3>
                <p className="text-neutral-600">
                  Not at all! Our content is designed for non-technical users. We explain everything jargon free.
                </p>
              </Card>

              <Card>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  How do expert consultations work?
                </h3>
                <p className="text-neutral-600">
                  If you are premium or corporate member you can book the consultation in at a time convenient for you through our integrated scheduling system.
                </p>
              </Card>

              <Card>
                <h3 className="font-semibold text-lg text-neutral-800 mb-3">
                  Is my data secure?
                </h3>
                <p className="text-neutral-600">
                  Absolutely. We use enterprise-grade security and are fully GDPR compliant. Your data is encrypted and never shared.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-primary to-secondary">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="font-montserrat font-bold text-3xl md:text-4xl mb-6">
              Are You Ready to Start Your AI Journey?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Take our free Skills Assessment and discover how AI can help you.
            </p>
            
            <Link to={ROUTES.register}>
              <Button variant="primary" size="lg">
                Unlock Your Learning Plan
              </Button>
            </Link>

            <p className="mt-6 text-sm opacity-75">
              No credit card required • Cancel anytime • 7-day money-back guarantee
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

